import { createContext, useCallback, useContext, useRef, useState } from 'react'
import axios from 'axios'
import reportJobService from '@api/v1/reportJobService'
import { savedRunErrorSentence, isRunOfReport, otherReportRunError } from '@components/reports/queue/resultRetention'

/**
 * Spec 168 — the report page a `?jobId=` reopen is shown ON: `{ reportId, jobId }`.
 *
 * Provided by ReportView at the ONE place all 21 specialized reports are mounted, so
 * every page's reopen is guarded without editing any of them (the ColumnAliasProvider
 * trick). Null anywhere else: no guard, exactly the old behaviour.
 *
 * Why it is needed: each page reads its saved run itself (`status(jid)` + `result(jid)`,
 * inside its first run) and never compared the run's report with its own. Measured on QA
 * 2026-10-12: `/reports/363?jobId=<an Aged A/R run>` drew that run's 69 rows on the
 * General Ledger page, under "Saved result — as of 10/12/2026, 5:36:07 PM".
 */
export const ReopenTargetContext = createContext(null)

/**
 * Spec 131 — only the NEWEST run may write the screen.
 *
 * The defect this closes: every report page committed a run's result in the order
 * the results ARRIVED, not the order the runs were STARTED. A filter change fires a
 * new run while the previous one is still out; on QA the newer request is often the
 * fastest (cb-api answers a repeated combination from its in-flight / 5-minute
 * dedupe at once), so the OLDER, uncached run lands last and silently replaces the
 * newer figures — under a filter panel that describes the newer run. Measured live
 * on P&L 35 and General Ledger 363, and in-code on Fee Receipts 22.
 *
 * General Ledger already had the cure (`runSeqRef`, CC-53890). This is that ticket,
 * extracted, so all 140 reports use one mechanism rather than one page guarding
 * itself and 19 not.
 *
 * Two layers over ONE sequence:
 *
 *   useRunTicket()  — the bare ticket, for a host that owns a lot of run state of
 *                     its own (ReportView.runReportWith).
 *   useLatestRun()  — ticket + the state every specialized page keeps (running,
 *                     error, and the criteria of the COMMITTED run), so a page
 *                     cannot gate its rows and forget its spinner or its error.
 *
 * What this deliberately does NOT do (spec 131 §5, decision 5): abort anything.
 * A superseded request is left to finish and its answer is dropped at the commit.
 * Three reasons, all measured: an aborted request never settles through
 * `client.js`'s interceptor (`axios.isCancel` → a promise that never resolves);
 * cb-api advances a job one step PER POLL, so a job whose polls stop sits in
 * `running` until the cron; and those stalled jobs count against the firm-wide
 * in-flight cap. Gating the commit is sufficient for correctness and touches none
 * of that.
 */
export function useRunTicket() {
  const seqRef = useRef(0)
  /* Takes the next ticket. `isCurrent()` stays true until anything else takes one
     or `invalidate()` retires it. */
  const begin = useCallback(() => {
    seqRef.current += 1
    const seq = seqRef.current
    return { seq, isCurrent: () => seq === seqRef.current }
  }, [])
  /* A refusal, a cancel, a dismiss, a report change: nothing in flight may land
     after this. Deliberately NOT called on unmount — StrictMode's simulated unmount
     would otherwise retire a run the remounted component still owns, and React 19
     already ignores a setState on an unmounted component. */
  const invalidate = useCallback(() => {
    seqRef.current += 1
  }, [])
  return { begin, invalidate }
}

const defaultErrorMessage = (err) => err?.message || 'Report failed'

/**
 * Spec 168 — let a reopen's commit through only if its run is THIS page's report's.
 *
 * `working` is the page's own reopen, already started: it must start synchronously
 * inside `run()`, because every page consumes its consume-once `jobRef` at the top of
 * that work, and a second run started while it waited would otherwise read the jobId
 * and reopen unguarded.
 *
 * The run's report is read AFTER the page's own reads settle, never beside them. Each
 * `GET /reports/jobs/:id` ADVANCES a job that is not finished, and the runner's claim on
 * a specialized job is read-then-write (`runner.js` CLAIM_MS), so two concurrent detail
 * reads of a queued job could both dispatch it. One more read, in sequence, costs one
 * round trip on a reopen and nothing else.
 *
 * Precedence: a run of another report is refused as such, whatever else failed; when the
 * report cannot be read, the page's own failure (if any) is what the page shows; a page
 * that read fine but whose run cannot be checked does not commit.
 */
async function settleReopen(working, { jobId, reportId }, isCurrent) {
  let commit
  let failure = null
  try { commit = await working } catch (err) { failure = err }
  // Superseded: the caller drops this run's answer and error alike.
  if (!isCurrent()) {
    if (failure) throw failure
    return commit
  }
  let job
  try { job = await reportJobService.status(jobId) } catch (err) { throw failure || err }
  if (!isRunOfReport(job, reportId)) throw otherReportRunError()
  if (failure) throw failure
  return commit
}

/**
 * @param initialRunning  true when the page opens on a `?jobId=` reopen, so the
 *                        spinner shows before the first run starts.
 * @param errorMessage    (err) => string. Each page keeps its OWN message (some
 *                        read CB's `response.data.error` first, AR has its own
 *                        fallback). Pass a stable (module-level) function. The one
 *                        exception is a saved run retention removed (spec 152,
 *                        `savedRunErrorSentence`), which every page words alike.
 *
 * Returns { running, error, ran, runKey, run, invalidate }:
 *
 *   ran     { seq, criteria } of the run whose rows are ON SCREEN; null before any
 *           run commits. `criteria` is what the page passed when the run STARTED —
 *           the shell's document surfaces (client CSV/XLSX/PDF header, print
 *           masthead, AI scope line) state it, so a document always names the run
 *           its rows came from, never a panel that has since moved.
 *   runKey  the ticket of the run most recently STARTED. Changes only at a start,
 *           never at a refusal — SpecializedFilterBar keys its stale-since-reset
 *           band on it, because `running` now stays true across a chain of
 *           superseding runs and so has no false→true edge to key on.
 */
export default function useLatestRun({ initialRunning = false, errorMessage = defaultErrorMessage } = {}) {
  const { begin, invalidate: retire } = useRunTicket()
  const [running, setRunning] = useState(initialRunning)
  const [error, setError] = useState(null)
  const [ran, setRan] = useState(null)
  const [runKey, setRunKey] = useState(0)
  // Spec 168: consume-once, like every page's own `jobRef`. ReportView keys each
  // specialized page on `reportId:jobId`, so a different reopen is a fresh mount.
  const reopenTarget = useContext(ReopenTargetContext)
  const reopenRef = useRef(reopenTarget?.jobId ? { jobId: reopenTarget.jobId, reportId: reopenTarget.reportId } : null)

  /**
   * `work({ isCurrent })` does the fetching and RETURNS A COMMIT FUNCTION (or
   * nothing): every setState that describes the rows. The commit runs only if this
   * is still the newest run, in the same batch as `running=false` and `ran`, so
   * the rows, the spinner and the document criteria change in one React commit.
   *
   * `criteria` is captured HERE, when the request is issued — the filters the
   * fetch was made with, not the ones on the panel when it lands.
   *
   * Resolves true when this run committed, false when it failed or was superseded.
   */
  const run = useCallback(async (work, { criteria = null } = {}) => {
    const t = begin()
    // Spec 168: a page's FIRST run is its `?jobId=` reopen — all 20 page files consume
    // their jobRef synchronously at the top of that run's work — so it is this one.
    const reopen = reopenRef.current
    reopenRef.current = null
    setRunning(true)
    setError(null)
    setRunKey(t.seq)
    try {
      const working = work({ isCurrent: t.isCurrent })
      const commit = reopen ? await settleReopen(working, reopen, t.isCurrent) : await working
      if (!t.isCurrent()) return false
      if (typeof commit === 'function') commit()
      setRan({ seq: t.seq, criteria })
      return true
    } catch (err) {
      // A superseded run's failure is not this screen's failure: the newer run
      // owns the error pane, and a red pane over the newer figures would be false.
      //
      // Nor is a CANCELLED request, even on the current run: nothing here aborts,
      // but a cancel from anywhere else (an interceptor, a future signal) is not a
      // report failure, and "canceled" in a red pane would read as one. Today
      // client.js's interceptor never lets a cancel settle at all; this is the
      // belt to that brace, not the thing that makes it safe.
      if (!t.isCurrent() || axios.isCancel(err)) return false
      // Spec 152 review — a saved run that retention removed (its stored result aged
      // out of the bucket: 410; its row deleted by the schedule's "last N": 404 "Job not
      // found") reads the same plain sentence on all 21 pages. Checked BEFORE the page's
      // own message, because six pages read `response.data.error` first and so showed
      // cb-api's bare "Result expired" / "Job not found" instead. Any other failure is
      // the page's own message, exactly as before. Spec 168: so is a run of another
      // report (`otherReportRunError`), refused by `settleReopen` above.
      setError(savedRunErrorSentence(err) || errorMessage(err))
      return false
    } finally {
      // Only the newest run may turn the spinner off. A superseded run finishing
      // first used to flip the control to "Refresh" and render the grid while the
      // run the panel describes was still out.
      if (t.isCurrent()) setRunning(false)
    }
  }, [begin, errorMessage])

  /**
   * Retire anything in flight and stop the spinner.
   *
   *   error     pass ONLY where the page shows an error today (a string, or null to
   *             clear one). Omitted = the error pane is left exactly as it is, so a
   *             silent refusal stays silent (spec 131 decision 6).
   *   clearRan  the page is also clearing its rows (GL's account refusal), so the
   *             documents must stop naming the run that produced them.
   */
  const invalidate = useCallback(({ error: nextError, clearRan = false } = {}) => {
    retire()
    setRunning(false)
    if (nextError !== undefined) setError(nextError)
    if (clearRan) setRan(null)
  }, [retire])

  return { running, error, ran, runKey, run, invalidate }
}
